// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Analytics
// Training compliance overview with metrics, charts, and tables
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Text,
  Group,
  SimpleGrid,
  Table,
  Badge,
  Loader,
  Center,
  Divider,
  Title,
  TextInput,
  Button,
  SegmentedControl,
  Progress,
  Modal,
  ScrollArea,
} from '../../components/gab/GabComponents';
import {
  IconUsers,
  IconBook,
  IconChartPie,
  IconAlertTriangle,
  IconSearch,
  IconDownload,
} from '@tabler/icons-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useBlackboardStore } from '../../store/blackboardStore';
import dayjs from 'dayjs';

// ── Constants ────────────────────────────────────────────────

const CHART_COLORS = [
  '#7589FF', '#08A847', '#C68700', '#D33423',
  '#A627FF', '#0285C4', '#03AC9E', '#F86A0B',
];

const DATE_RANGES = [
  { label: '7d', value: '7' },
  { label: '30d', value: '30' },
  { label: '90d', value: '90' },
  { label: 'All Time', value: 'all' },
];

// ── Helpers ──────────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Box mb="sm">
      <Text size="10px" c="gray.6" tt="uppercase" fw={700} style={{ letterSpacing: 2 }}>
        {children}
      </Text>
      <Divider mt={6} color="gray.3" />
    </Box>
  );
}

function StatCard({ title, value, icon: Icon, color, onClick }: {
  title: string;
  value: string | number;
  icon: React.FC<{ size?: number; style?: React.CSSProperties }>;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Paper
      p="sm"
      withBorder
      shadow="sm"
      radius="md"
      ta="center"
      style={{
        borderTop: `3px solid ${color}`,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <Icon size={16} style={{ color, marginBottom: 4 }} />
      <Text fz={28} fw={700}>{value}</Text>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{title}</Text>
    </Paper>
  );
}

function complianceBarColor(pct: number): string {
  if (pct >= 90) return '#08A847';
  if (pct >= 70) return '#C68700';
  if (pct >= 50) return '#F86A0B';
  return '#D33423';
}

function renderPieLabel(props: any) {
  return `${props.name} (${(props.percent * 100).toFixed(0)}%)`;
}

// ── Component ────────────────────────────────────────────────

export default function Analytics() {
  const {
    users,
    trainings,
    trainingRecords,
    loadUsers,
    loadTrainings,
    loadTrainingRecords,
  } = useBlackboardStore();

  const [initialLoad, setInitialLoad] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [personnelSearch, setPersonnelSearch] = useState('');
  const [drillDown, setDrillDown] = useState<{ title: string; type: string } | null>(null);
  const [showAllTrainings, setShowAllTrainings] = useState(false);

  useEffect(() => {
    async function load() {
      await Promise.all([loadUsers(), loadTrainings(), loadTrainingRecords()]);
      setInitialLoad(false);
    }
    load();
  }, [loadUsers, loadTrainings, loadTrainingRecords]);

  const now = dayjs();

  // Filter records by date range
  const filteredRecords = useMemo(() => {
    if (dateRange === 'all') return trainingRecords;
    const cutoff = now.subtract(parseInt(dateRange, 10), 'day');
    return trainingRecords.filter(r => {
      const d = r.completedDate || r.datecreated;
      return d && dayjs(d).isAfter(cutoff);
    });
  }, [trainingRecords, dateRange, now]);

  // ── Key Metrics ────────────────────────────────────────────

  const stats = useMemo(() => {
    const totalPersonnel = users.length;
    const activeTrainings = trainings.filter(t => t.active === 'true' || t.active === '1').length;

    const totalRecords = trainingRecords.length;
    const compliantRecords = trainingRecords.filter(r => {
      if (!r.completedDate) return false;
      if (!r.expiresDate) return true;
      return dayjs(r.expiresDate).isAfter(now);
    }).length;
    const complianceRate = totalRecords > 0 ? Math.round((compliantRecords / totalRecords) * 100) : 100;

    const expiringSoon = trainingRecords.filter(r => {
      if (!r.expiresDate) return false;
      const exp = dayjs(r.expiresDate);
      return exp.isAfter(now) && exp.diff(now, 'day') <= 30;
    }).length;

    return { totalPersonnel, activeTrainings, complianceRate, expiringSoon };
  }, [users, trainings, trainingRecords, now]);

  // ── Completions by Category (pie chart) ────────────────────

  const completionsByCategory = useMemo(() => {
    const catCounts: Record<string, number> = {};
    filteredRecords.forEach(r => {
      if (!r.completedDate) return;
      const training = trainings.find(t => String(t.id) === r.trainingId);
      const cat = training?.category || 'Uncategorized';
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    });
    return Object.entries(catCounts).map(([name, value]) => ({ name, value }));
  }, [filteredRecords, trainings]);

  // ── Category distribution (bar chart) ──────────────────────

  const categoryDistribution = useMemo(() => {
    const cats: Record<string, number> = {};
    trainings.forEach(t => {
      const cat = t.category || 'Uncategorized';
      cats[cat] = (cats[cat] || 0) + 1;
    });
    return Object.entries(cats).map(([name, count]) => ({ name, count }));
  }, [trainings]);

  // ── Compliance by Training (table) ─────────────────────────

  const complianceByTraining = useMemo(() => {
    return trainings.map(t => {
      const tRecords = trainingRecords.filter(r => r.trainingId === String(t.id));
      const totalAssigned = tRecords.length;
      const completed = tRecords.filter(r => {
        if (!r.completedDate) return false;
        if (!r.expiresDate) return true;
        return dayjs(r.expiresDate).isAfter(now);
      }).length;
      const compliancePct = totalAssigned > 0 ? Math.round((completed / totalAssigned) * 100) : 0;
      return {
        id: t.id,
        name: t.name || String(t.id),
        category: t.category || '\u2014',
        totalAssigned,
        completed,
        compliancePct,
      };
    });
  }, [trainings, trainingRecords, now]);

  // ── Personnel Compliance (table) ───────────────────────────

  const personnelCompliance = useMemo(() => {
    let rows = users.map(u => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
      const uRecords = trainingRecords.filter(r => r.userId === String(u.id) || r.userId === u.email || r.userName === name);
      const totalRequired = uRecords.length;
      const completed = uRecords.filter(r => {
        if (!r.completedDate) return false;
        if (!r.expiresDate) return true;
        return dayjs(r.expiresDate).isAfter(now);
      }).length;
      const compliancePct = totalRequired > 0 ? Math.round((completed / totalRequired) * 100) : 100;

      let status = 'compliant';
      if (compliancePct < 50) status = 'critical';
      else if (compliancePct < 80) status = 'at-risk';
      else if (compliancePct < 100) status = 'partial';

      return { id: u.id, name, role: u.role || 'student', totalRequired, completed, compliancePct, status };
    });

    if (personnelSearch) {
      const q = personnelSearch.toLowerCase();
      rows = rows.filter(r => (r.name || '').toLowerCase().includes(q) || (r.role || '').toLowerCase().includes(q));
    }

    return rows;
  }, [users, trainingRecords, now, personnelSearch]);

  // ── Drill-down data ──────────────────────────────────────────

  const drillDownRows = useMemo(() => {
    if (!drillDown) return [];

    const userMap: Record<string, string> = {};
    users.forEach(u => {
      userMap[String(u.id)] = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
    });

    if (drillDown.type === 'expiring') {
      return trainingRecords
        .filter(r => {
          if (!r.expiresDate) return false;
          const exp = dayjs(r.expiresDate);
          return exp.isAfter(now) && exp.diff(now, 'day') <= 30;
        })
        .map(r => ({
          name: r.userName || userMap[r.userId] || 'Unknown',
          training: r.trainingName || r.trainingId || '--',
          date: r.expiresDate ? dayjs(r.expiresDate).format('MMM D, YYYY') : '--',
          daysLeft: r.expiresDate ? dayjs(r.expiresDate).diff(now, 'day') : 0,
        }))
        .sort((a, b) => a.daysLeft - b.daysLeft);
    }

    if (drillDown.type === 'compliance') {
      const nonCompliant = trainingRecords.filter(r => {
        if (!r.completedDate) return true;
        if (!r.expiresDate) return false;
        return dayjs(r.expiresDate).isBefore(now);
      });
      return nonCompliant.map(r => ({
        name: r.userName || userMap[r.userId] || 'Unknown',
        training: r.trainingName || r.trainingId || '--',
        date: r.expiresDate ? dayjs(r.expiresDate).format('MMM D, YYYY') : 'Never completed',
        daysLeft: r.expiresDate ? dayjs(r.expiresDate).diff(now, 'day') : -999,
      })).sort((a, b) => a.daysLeft - b.daysLeft);
    }

    if (drillDown.type === 'personnel') {
      return users.map(u => ({
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
        training: u.role || 'student',
        date: '',
        daysLeft: 0,
      }));
    }

    if (drillDown.type === 'trainings') {
      return trainings
        .filter(t => t.active === 'true' || t.active === '1')
        .map(t => ({
          name: t.name,
          training: t.category || 'Uncategorized',
          date: t.frequencyDays > 0 ? `Every ${t.frequencyDays} days` : 'No expiry',
          daysLeft: 0,
        }));
    }

    return [];
  }, [drillDown, trainingRecords, users, trainings, now]);

  // ── CSV Export ─────────────────────────────────────────────

  const exportCSV = useCallback(() => {
    const header = ['Name', 'Role', 'Total Required', 'Completed', 'Compliance %', 'Status'];
    const csvRows = [header.join(',')];
    personnelCompliance.forEach(row => {
      csvRows.push([
        `"${row.name}"`,
        row.role,
        row.totalRequired,
        row.completed,
        row.compliancePct,
        row.status,
      ].join(','));
    });
    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `personnel_compliance_${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [personnelCompliance]);

  // ── Status badge ───────────────────────────────────────────

  function complianceStatusBadge(status: string) {
    const map: Record<string, { color: string; label: string }> = {
      compliant: { color: 'var(--color-success)', label: 'Compliant' },
      partial: { color: '#C68700', label: 'Partial' },
      'at-risk': { color: '#F86A0B', label: 'At Risk' },
      critical: { color: 'var(--color-error)', label: 'Critical' },
    };
    const cfg = map[status] || { color: '#939598', label: status };
    return <Badge color={cfg.color}>{cfg.label}</Badge>;
  }

  // ── Render ─────────────────────────────────────────────────

  if (initialLoad) {
    return (
      <Center h={400}>
        <Loader size="lg" color="var(--color-primary-700)" />
      </Center>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Title order={3} fw={700}>
            Analytics
          </Title>
          <Text size="sm" c="dimmed" mt={2}>
            Training compliance overview
          </Text>
        </Box>
        <SegmentedControl value={dateRange} onChange={setDateRange} data={DATE_RANGES} size="xs" />
      </Group>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <StatCard title="Total Personnel" value={stats.totalPersonnel} icon={IconUsers} color="#7589FF"
          onClick={() => setDrillDown({ title: 'All Personnel', type: 'personnel' })} />
        <StatCard title="Active Trainings" value={stats.activeTrainings} icon={IconBook} color="#0285C4"
          onClick={() => setDrillDown({ title: 'Active Trainings', type: 'trainings' })} />
        <StatCard title="Compliance Rate" value={`${stats.complianceRate}%`} icon={IconChartPie} color="#08A847"
          onClick={() => setDrillDown({ title: 'Non-Compliant Records', type: 'compliance' })} />
        <StatCard title="Expiring Soon" value={stats.expiringSoon} icon={IconAlertTriangle} color="#D33423"
          onClick={() => setDrillDown({ title: 'Expiring Within 30 Days', type: 'expiring' })} />
      </SimpleGrid>

      {/* Charts */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" mt="lg">
        {/* Category Distribution (Bar) */}
        <Paper p="md" withBorder shadow="sm" radius="md" style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHeader>Training Category Distribution</SectionHeader>
          {categoryDistribution.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="lg">
              No trainings found
            </Text>
          ) : (
            <div style={{ flex: 1, minHeight: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryDistribution} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-30} textAnchor="end" />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#7589FF" radius={[4, 4, 0, 0]}>
                    {categoryDistribution.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Paper>

        {/* Completions by Category (Pie) */}
        <Paper p="md" withBorder shadow="sm" radius="md" style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHeader>Training Completions by Category</SectionHeader>
          {completionsByCategory.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="lg">
              No completions in this period
            </Text>
          ) : (
            <div style={{ flex: 1, minHeight: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={completionsByCategory}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={45}
                    dataKey="value"
                    label={renderPieLabel}
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {completionsByCategory.map((entry, idx) => (
                      <Cell key={entry.name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Paper>
      </SimpleGrid>

      {/* Compliance Distribution + Position Compliance Charts */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" mt="lg">
        {/* Compliance Distribution Donut */}
        <Paper p="md" withBorder shadow="sm" radius="md" style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHeader>Personnel Compliance Distribution</SectionHeader>
          {(() => {
            const dist = [
              { name: 'Compliant', value: personnelCompliance.filter(p => p.status === 'compliant').length },
              { name: 'Partial', value: personnelCompliance.filter(p => p.status === 'partial').length },
              { name: 'At Risk', value: personnelCompliance.filter(p => p.status === 'at-risk').length },
              { name: 'Critical', value: personnelCompliance.filter(p => p.status === 'critical').length },
            ].filter(d => d.value > 0);
            const distColors = ['#08A847', '#C68700', '#F86A0B', '#D33423'];
            return dist.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">No data</Text>
            ) : (
              <div style={{ flex: 1, minHeight: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dist} cx="50%" cy="50%" outerRadius={90} innerRadius={45} dataKey="value" label={renderPieLabel} labelLine={{ strokeWidth: 1 }}>
                      {dist.map((_, idx) => <Cell key={idx} fill={distColors[idx % distColors.length]} />)}
                    </Pie>
                    <RechartsTooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
        </Paper>

        {/* Completions over Time (bar) */}
        <Paper p="md" withBorder shadow="sm" radius="md" style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHeader>Recent Completions (Last 12 Weeks)</SectionHeader>
          {(() => {
            const weeks: { name: string; count: number }[] = [];
            for (let i = 11; i >= 0; i--) {
              const weekStart = dayjs().subtract(i, 'week').startOf('week');
              const weekEnd = weekStart.endOf('week');
              const count = trainingRecords.filter(r => {
                if (!r.completedDate) return false;
                const d = dayjs(r.completedDate);
                return d.isAfter(weekStart) && d.isBefore(weekEnd);
              }).length;
              weeks.push({ name: weekStart.format('M/D'), count });
            }
            return (
              <div style={{ flex: 1, minHeight: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeks} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#7589FF" radius={[4, 4, 0, 0]} name="Completions" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
        </Paper>
      </SimpleGrid>

      {/* Compliance by Training — collapsible, shows top 10 by default */}
      <Paper p="md" withBorder shadow="sm" radius="md" mt="lg">
        <Group justify="space-between" mb="sm">
          <SectionHeader>Compliance by Training</SectionHeader>
          {complianceByTraining.length > 10 && (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setShowAllTrainings(prev => !prev)}
            >
              {showAllTrainings ? 'Show Top 10' : `Show All (${complianceByTraining.length})`}
            </Button>
          )}
        </Group>
        {complianceByTraining.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="lg">
            No training definitions found
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={700}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Training</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Assigned</Table.Th>
                  <Table.Th>Completed</Table.Th>
                  <Table.Th>Compliance</Table.Th>
                  <Table.Th style={{ minWidth: 120 }}>Progress</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(showAllTrainings ? complianceByTraining : complianceByTraining.slice(0, 10)).map(row => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{row.name}</Text>
                    </Table.Td>
                    <Table.Td>{row.category}</Table.Td>
                    <Table.Td>{row.totalAssigned}</Table.Td>
                    <Table.Td>{row.completed}</Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        fw={600}
                        c={row.compliancePct >= 90 ? 'var(--color-success)' : row.compliancePct >= 70 ? 'yellow.8' : 'var(--color-error)'}
                      >
                        {row.compliancePct}%
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Progress value={row.compliancePct} size="sm" radius="xl" color={complianceBarColor(row.compliancePct)} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      {/* Personnel Compliance */}
      <Paper p="md" withBorder shadow="sm" radius="md" mt="lg">
        <Group justify="space-between" align="center" mb="sm">
          <SectionHeader>Personnel Compliance</SectionHeader>
          <Group gap="sm">
            <TextInput
              placeholder="Search by name or role..."
              size="xs"
              value={personnelSearch}
              onChange={e => setPersonnelSearch(e.target.value)}
              leftSection={<IconSearch size={12} />}
              style={{ width: 240 }}
            />
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDownload size={12} />}
              onClick={exportCSV}
            >
              Export CSV
            </Button>
          </Group>
        </Group>

        {personnelCompliance.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="lg">
            No personnel found
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={700} style={{ maxHeight: 400, overflowY: 'auto' }}>
            <Table stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Required</Table.Th>
                  <Table.Th>Completed</Table.Th>
                  <Table.Th>Compliance</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {personnelCompliance.map(row => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{row.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm" color="var(--color-primary-700)">{row.role}</Badge>
                    </Table.Td>
                    <Table.Td>{row.totalRequired}</Table.Td>
                    <Table.Td>{row.completed}</Table.Td>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <Text
                          size="sm"
                          fw={600}
                          c={row.compliancePct >= 90 ? 'var(--color-success)' : row.compliancePct >= 70 ? 'yellow.8' : 'var(--color-error)'}
                        >
                          {row.compliancePct}%
                        </Text>
                        <Progress
                          value={row.compliancePct}
                          size="sm"
                          radius="xl"
                          color={complianceBarColor(row.compliancePct)}
                          style={{ flex: 1, minWidth: 60 }}
                        />
                      </Group>
                    </Table.Td>
                    <Table.Td>{complianceStatusBadge(row.status)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      {/* Drill-down Modal */}
      <Modal
        opened={!!drillDown}
        onClose={() => setDrillDown(null)}
        title={<Text fw={600}>{drillDown?.title || 'Details'}</Text>}
        size="lg"
      >
        {drillDownRows.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">No records found.</Text>
        ) : (
          <ScrollArea h={400}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{drillDown?.type === 'trainings' ? 'Training' : 'Name'}</Table.Th>
                  <Table.Th>{drillDown?.type === 'trainings' ? 'Category' : drillDown?.type === 'personnel' ? 'Role' : 'Training'}</Table.Th>
                  {drillDown?.type !== 'personnel' && (
                    <Table.Th>{drillDown?.type === 'trainings' ? 'Frequency' : 'Date'}</Table.Th>
                  )}
                  {drillDown?.type === 'expiring' && <Table.Th>Days Left</Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {drillDownRows.map((row, idx) => (
                  <Table.Tr key={idx}>
                    <Table.Td><Text size="sm" fw={500}>{row.name}</Text></Table.Td>
                    <Table.Td><Text size="sm">{row.training}</Text></Table.Td>
                    {drillDown?.type !== 'personnel' && (
                      <Table.Td><Text size="sm">{row.date}</Text></Table.Td>
                    )}
                    {drillDown?.type === 'expiring' && (
                      <Table.Td>
                        <Badge color={row.daysLeft <= 7 ? 'var(--color-error)' : row.daysLeft <= 14 ? '#F86A0B' : '#C68700'} size="sm">
                          {row.daysLeft} days
                        </Badge>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
        <Group justify="space-between" mt="md">
          <Text size="xs" c="dimmed">{drillDownRows.length} record(s)</Text>
          <Button variant="default" size="xs" onClick={() => setDrillDown(null)}>Close</Button>
        </Group>
      </Modal>
    </Box>
  );
}
